import React, { useEffect, useState } from 'react';
import Hero from '../components/Hero';
import StatsSection from '../components/StatsSection';
import ImpactCalculator from '../components/ImpactCalculator';
import { Reveal } from '../components/Reveal';
import PauseOnScroll from '../components/PauseOnScroll';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Truck, Cloud, Wallet, Clock, Sparkles, Heart, Users, Droplets, Award, Shield, Sun, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { fetchArticles } from '../lib/api';
import { useSection, resolveImage } from '../contexts/ContentContext';

const ICON_MAP = { Leaf, Truck, Cloud, Wallet, Clock, Sparkles, Droplets, Heart, Award, Shield, Users, Sun };

export default function Home() {
  const home = useSection('home');
  const settings = useSection('settings');
  const [blogPosts, setBlogPosts] = useState([]);
  const [videoPlaying, setVideoPlaying] = useState(false);

  useEffect(() => {
    fetchArticles({ limit: 3 }).then((data) => setBlogPosts(data || [])).catch(() => {});
  }, []);

  const about = home.about || {};
  const videoIntro = home.videoIntro || {};
  const videoPreviewImg = resolveImage(videoIntro.previewImage);
  const benefits = home.benefits || {};
  const steps = home.steps || {};
  const testimonials = home.testimonials || {};
  const partners = home.partners || {};
  const cta = home.cta || {};
  const blogTeaser = home.blogTeaser || {};

  return (
    <>
      <Hero />

      {/* Vidéo Journal local — Ce qu'est un Kiosque à Eau */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-sky-950 via-sky-900 to-emerald-950 relative overflow-hidden" data-testid="home-video-intro">
        <div className="absolute -top-20 -left-20 w-72 h-72 bg-sky-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-400 text-slate-900 text-xs font-bold uppercase tracking-wider mb-4 shadow-lg">
              <Sparkles className="w-3.5 h-3.5" />
              Vu au journal
            </span>
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-white mb-3">
              Découvrez le <span className="text-emerald-300">Kiosque à Eau</span> en vidéo
            </h2>
            <p className="text-sky-200 mb-8 max-w-2xl mx-auto">
              Le journal local est venu filmer le premier Kiosque à Eau de France installé à Pau-Lons.
            </p>
            <div className="relative w-full max-w-3xl mx-auto aspect-video rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10 bg-black">
              <PauseOnScroll
                className="absolute inset-0"
                onLeave={() => setVideoPlaying(false)}
              >
                {videoPlaying ? (
                  <iframe
                    src="//www.ultimedia.com/deliver/generic/iframe/mdtk/01722489/src/xspulmf/zone/1/showtitle/1/autoplay/1/"
                    title="Kiosques à Eau — Journal local"
                    className="w-full h-full"
                    frameBorder="0"
                    scrolling="no"
                    allow="autoplay; fullscreen"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    data-testid="home-video-iframe"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setVideoPlaying(true)}
                    className="group absolute inset-0 w-full h-full flex items-center justify-center text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
                    aria-label="Lire la vidéo Kiosques à Eau — Journal local"
                    data-testid="home-video-play-btn"
                    style={
                      videoPreviewImg
                        ? {
                            backgroundImage: `url("${videoPreviewImg}")`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                          }
                        : {
                            background:
                              "radial-gradient(ellipse at center, rgba(2,132,199,0.35) 0%, rgba(6,78,59,0.85) 60%, rgba(15,23,42,0.95) 100%)",
                          }
                    }
                  >
                    {videoPreviewImg && (
                      <span
                        className="absolute inset-0 bg-gradient-to-br from-sky-950/40 via-sky-900/30 to-emerald-950/50 group-hover:from-sky-950/30 group-hover:to-emerald-950/40 transition-colors"
                        aria-hidden="true"
                      />
                    )}
                    <span className="relative z-10 flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/95 backdrop-blur shadow-2xl shadow-black/40 group-hover:scale-105 group-active:scale-100 transition-transform">
                      <span className="absolute inset-0 rounded-full bg-gradient-to-br from-sky-400/40 to-emerald-400/40 blur-xl -z-10 opacity-70 group-hover:opacity-100 transition-opacity"></span>
                      <Play className="w-9 h-9 sm:w-11 sm:h-11 text-sky-900 fill-sky-900 translate-x-0.5" strokeWidth={2} />
                    </span>
                  </button>
                )}
              </PauseOnScroll>
            </div>
          </Reveal>
        </div>
      </section>

      <StatsSection />

      {/* About */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-emerald-50 via-white to-sky-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <div className="relative">
                {about.image && (
                  <div className="aspect-square rounded-[2.5rem] overflow-hidden shadow-xl">
                    <img src={resolveImage(about.image)} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                {(about.convictionTitle || about.convictionText) && (
                  <div
                    className="mt-5 lg:mt-0 bg-white rounded-2xl shadow-xl border border-sky-100 p-5 max-w-full lg:max-w-xs lg:absolute lg:-top-6 lg:-right-6 lg:z-10"
                    data-testid="home-conviction-card"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-sky-400 flex items-center justify-center shrink-0">
                        <Heart className="w-5 h-5 text-white fill-white" />
                      </div>
                      <span className="font-display font-bold text-sky-950">{about.convictionTitle}</span>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">{about.convictionText}</p>
                  </div>
                )}
              </div>
            </Reveal>

            <Reveal delay={120}>
              {about.eyebrow && <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">{about.eyebrow}</span>}
              <h2 className="font-display font-extrabold text-4xl lg:text-5xl text-sky-950 mb-5 leading-tight">
                {about.title} <span className="gradient-text">{about.titleHighlight}</span>
              </h2>
              {about.paragraph1 && <p className="text-lg text-slate-600 leading-relaxed mb-5" dangerouslySetInnerHTML={{ __html: about.paragraph1 }} />}
              {about.paragraph2 && <p className="text-base text-slate-600 leading-relaxed mb-8" dangerouslySetInnerHTML={{ __html: about.paragraph2 }} />}
              <div className="flex flex-wrap gap-4">
                {about.primaryCtaLabel && (
                  <Link to={about.primaryCtaLink || '/association'}>
                    <Button className="bg-sky-900 hover:bg-sky-950 text-white rounded-xl px-6">{about.primaryCtaLabel}<ArrowRight className="ml-2 w-4 h-4" /></Button>
                  </Link>
                )}
                {about.secondaryCtaLabel && (
                  <Link to={about.secondaryCtaLink || '/soutenir'}>
                    <Button variant="outline" className="border-2 border-sky-200 text-sky-900 hover:bg-sky-50 rounded-xl px-6">{about.secondaryCtaLabel}</Button>
                  </Link>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-14">
              {benefits.eyebrow && <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">{benefits.eyebrow}</span>}
              <h2 className="font-display font-extrabold text-4xl lg:text-5xl text-sky-950 mb-5">
                {benefits.title} <span className="gradient-text">{benefits.titleHighlight}</span>
              </h2>
              <p className="text-lg text-slate-600">{benefits.description}</p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {(benefits.items || []).map((b, i) => {
              const Icon = ICON_MAP[b.icon] || Leaf;
              return (
                <Reveal key={i} delay={i * 60}>
                  <div className="group p-7 rounded-3xl border border-sky-100 hover:border-sky-200 hover:shadow-lg hover:shadow-sky-100/60 hover:-translate-y-1 transition-all duration-300 bg-gradient-to-br from-white to-sky-50/30">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center mb-5 group-hover:from-sky-200 group-hover:to-emerald-200 transition-colors">
                      <Icon className="w-7 h-7 text-sky-700" strokeWidth={2.2} />
                    </div>
                    <h3 className="font-display font-bold text-xl text-sky-950 mb-2">{b.title}</h3>
                    <p className="text-slate-600 leading-relaxed text-sm">{b.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <ImpactCalculator />

      {/* How it works snippet */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-14">
              {steps.eyebrow && <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">{steps.eyebrow}</span>}
              <h2 className="font-display font-extrabold text-4xl lg:text-5xl text-sky-950 mb-5">
                {steps.title} <span className="gradient-text">{steps.titleHighlight}</span>
              </h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {(steps.items || []).map((s, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="relative p-6 rounded-3xl bg-gradient-to-br from-sky-50 to-emerald-50/50 border border-sky-100 h-full">
                  <div className="absolute -top-4 left-6 px-3 py-1 rounded-full bg-gradient-to-r from-sky-500 to-emerald-500 text-white text-xs font-display font-extrabold">
                    {s.number}
                  </div>
                  <h3 className="font-display font-bold text-lg text-sky-950 mt-3 mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.description}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="text-center mt-12">
              <Link to="/premierkiosqueàeauàpau">
                <Button size="lg" className="bg-gradient-to-r from-sky-500 to-emerald-500 text-white rounded-xl px-7">
                  En savoir plus<ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-sky-950 to-emerald-950 text-white relative overflow-hidden">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-sky-500/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-14">
              {testimonials.eyebrow && <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-300 mb-3">{testimonials.eyebrow}</span>}
              <h2 className="font-display font-extrabold text-4xl lg:text-5xl text-white mb-5">{testimonials.title}</h2>
              <p className="text-lg text-sky-200">{testimonials.description}</p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {(testimonials.items || []).map((t, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="p-7 rounded-3xl bg-white/5 backdrop-blur border border-white/10 hover:border-white/20 transition-colors h-full">
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: Number(t.rating) || 5 }).map((_, j) => (
                      <Sparkles key={j} className="w-4 h-4 text-amber-300 fill-amber-300" />
                    ))}
                  </div>
                  <p className="text-sky-100 leading-relaxed mb-6 italic">« {t.text} »</p>
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-sky-400 to-emerald-400 flex items-center justify-center font-bold text-white text-sm">
                      {t.avatar}
                    </div>
                    <div>
                      <p className="font-semibold text-white text-sm">{t.name}</p>
                      <p className="text-xs text-sky-300">{t.city}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Blog teaser */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
              <div>
                {blogTeaser.eyebrow && <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">{blogTeaser.eyebrow}</span>}
                <h2 className="font-display font-extrabold text-4xl lg:text-5xl text-sky-950">{blogTeaser.title || 'Notre blog'}</h2>
              </div>
              <Link to="/blog" className="text-sky-700 font-semibold hover:text-sky-900 inline-flex items-center gap-1">
                Voir tous les articles<ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {blogPosts.slice(0, 3).map((p, i) => (
              <Reveal key={p.id} delay={i * 100}>
                <Link to={`/blog/${p.slug}`} className="group block">
                  <article className="rounded-3xl overflow-hidden bg-white border border-sky-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                    <div className="aspect-[16/10] overflow-hidden">
                      {p.image && <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                        <span className="px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 font-semibold">{p.category}</span>
                        <span>{p.readTime}</span>
                      </div>
                      <h3 className="font-display font-bold text-lg text-sky-950 mb-2 group-hover:text-sky-700 transition-colors leading-snug">{p.title}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed line-clamp-2">{p.excerpt}</p>
                    </div>
                  </article>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section data-testid="partners-section" className="py-20 bg-gradient-to-b from-white to-sky-50/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-14">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">
                Ils nous soutiennent
              </span>
              <h2 className="font-display font-extrabold text-4xl lg:text-5xl text-sky-950">
                Nos <span className="gradient-text">partenaires</span>
              </h2>
            </div>
          </Reveal>

          {(partners.groups || []).map((group, gi) => (
            <Reveal key={gi} delay={gi * 80}>
              <div className="mb-12 last:mb-0" data-testid={`partners-group-${gi}`}>
                <h3 data-testid={`partners-group-title-${gi}`} className="text-center font-display font-bold text-2xl text-sky-900 mb-2">
                  {group.title}
                </h3>
                {group.subtitle && (
                  <p className="text-center text-sm text-slate-500 mb-8">{group.subtitle}</p>
                )}
                <div data-testid={`partners-grid-${gi}`} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                  {(group.items || []).map((p, i) => (
                    <div
                      key={i}
                      data-testid={`partner-card-${gi}-${i}`}
                      className="group flex flex-col items-center justify-start p-5 rounded-2xl bg-white border border-sky-100 hover:shadow-lg hover:shadow-sky-100/50 hover:-translate-y-1 hover:border-sky-200 transition-all text-center"
                    >
                      <div className="w-full h-16 flex items-center justify-center mb-3">
                        {p.logo ? (
                          <img
                            src={resolveImage(p.logo)}
                            alt={p.name || 'Partenaire'}
                            className="max-h-full max-w-full object-contain grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300"
                          />
                        ) : (
                          <Users className="w-8 h-8 text-slate-300" />
                        )}
                      </div>
                      {p.name && (
                        <p className="font-semibold text-xs text-sky-950 leading-tight">{p.name}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-100 via-white to-emerald-100"></div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            {cta.eyebrow && <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">{cta.eyebrow}</span>}
            <h2 className="font-display font-extrabold text-4xl lg:text-6xl text-sky-950 mb-6 leading-tight">
              {cta.title} <span className="gradient-text">{cta.titleHighlight}</span> {cta.titleSuffix}
            </h2>
            <p className="text-lg text-slate-700 max-w-2xl mx-auto mb-10">{cta.description}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {cta.primaryCtaLabel && (
                <Link to={cta.primaryCtaLink || '/soutenir'}>
                  <Button size="lg" className="water-btn bg-gradient-to-r from-sky-500 to-emerald-500 text-white font-semibold px-8 py-6 rounded-2xl text-base shadow-lg shadow-sky-200 border-0">
                    {cta.primaryCtaLabel}<ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              )}
              {cta.secondaryCtaLabel && (
                <Link to={cta.secondaryCtaLink || '/je-soutiens'}>
                  <Button size="lg" variant="outline" className="border-2 border-sky-300 text-sky-900 font-semibold px-8 py-6 rounded-2xl text-base hover:bg-white gap-2">
                    <Heart className="w-4 h-4" />{cta.secondaryCtaLabel}
                  </Button>
                </Link>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
